<script setup lang="ts">
import AppCard from '@/components/card/AppCard.vue'
import { FuelCalculatorPage } from '@/types/types'

useSeoMeta({
  title: () => `Drivmedla - drivmedelskalkylator`,
  description: () => 'Jämför klimatpåverkan, energiinnehåll och kostnad för olika drivmedel',
  ogTitle: () => `Drivmedla - Drivmedelskalkylator`,
  ogDescription: () => 'Jämför klimatpåverkan, energiinnehåll och kostnad för olika drivmedel',
  ogLocale: () => 'sv_SE',
  ogSiteName: () => 'Drivmedla',
  ogUrl: () => `${useRuntimeConfig().public.baseURL}${useRoute().fullPath}`,
  ogImage: () => `${useRuntimeConfig().public.baseURL}/_nuxt/assets/_logo-horizontal-GREEN.svg`,
})
const { findOne } = useStrapi()
const { data: pageInfo } = await useAsyncData(
  'calculator-page',
  () => findOne<FuelCalculatorPage>('calculator-page', { populate: '*' }),
  { transform: (_pageInfo) => _pageInfo.data }
)

const {
  selectedAmount,
  selectedFuel,
  selectedCost,
  result,
  comparison,
  fuelData,
  comparisonDimension,
  resetCalculator,
} = useFuelCalulator()

const checked = ref<boolean>(false)

function getSuffix(price?: string) {
  price = price ?? ''

  if (selectedFuel.value?.unit) {
    return `${price}/${selectedFuel.value?.unit}`
  } else {
    return `${price}`
  }
}

function formatNumber(number: number) {
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
    maximumSignificantDigits: 3,
    minimumSignificantDigits: 1,
  }).format(number)
}
function updateSelectedAmount(e: any) {
  selectedAmount.value = e.value
}
function updateSelectedCost(e: any) {
  selectedCost.value = e.value
}
function resetCalc() {
  resetCalculator()
  checked.value = false
}
</script>
<template>
  <main>
    <AppHero
      :title="pageInfo?.attributes?.generalContent?.title"
      :description="pageInfo?.attributes?.generalContent.ingress"
      background-color="#EBBE5D"
    />
    <LayoutSection>
      <div class="flex justify-content-center my-2">
        <div class="col-12">
          <AppCard>
            <div class="grid">
              <section class="col">
                <h2>Drivmedelskalkylator</h2>
                <p>
                  Beräkna klimatpåverkan, energianvändning och kostnad för olika drivmedel. Ange
                  drivmedel, mängd och pris för att få resultat.
                </p>
                <form>
                  <div class="flex flex-column my-2">
                    <label id="name" class="font-bold text-sm my-1"
                      >Drivmedel <span class="text-red-500">*</span></label
                    >
                    <Dropdown
                      v-model="selectedFuel"
                      :options="fuelData"
                      option-label="name"
                      placeholder="Välj drivmedel"
                      aria-labelledby="name"
                      class="w-full md:w-14rem"
                    />
                  </div>
                  <div v-if="selectedFuel">
                    <div class="flex flex-column my-2">
                      <label for="amount" class="font-bold text-sm my-1"
                        >Mängd <span class="text-red-500">*</span></label
                      >
                      <InputNumber
                        v-model="selectedAmount"
                        input-id="amount"
                        class="w-full md:w-14rem"
                        :min-fraction-digits="0"
                        :max-fraction-digits="2"
                        locale="sv-SE"
                        :placeholder="`Ange antal ${selectedFuel?.unit || ''}`"
                        :suffix="` ${selectedFuel?.unit}`"
                        @input="updateSelectedAmount"
                      />
                    </div>
                    <div class="my-3">
                      <Checkbox v-model="checked" input-id="costCheck" :binary="true" />
                      <label for="costCheck" class="text-sm mx-2">Jag vill beräkna kostnad</label>
                    </div>
                    <div v-if="checked" class="flex flex-column my-2">
                      <label for="cost" class="font-bold text-sm my-1">Kostnad</label>
                      <InputNumber
                        v-model="selectedCost"
                        input-id="cost"
                        class="w-full md:w-14rem"
                        :placeholder="`Ange kostnad${getSuffix()}`"
                        :min-fraction-digits="0"
                        :max-fraction-digits="2"
                        locale="sv-SE"
                        :suffix="` ${getSuffix('SEK')}`"
                        @input="updateSelectedCost"
                      />
                    </div>
                  </div>
                </form>
                <Button
                  v-if="result"
                  size="small"
                  severity="secondary"
                  class="my-2"
                  @click="resetCalc"
                >
                  Nollställ kalkylator
                </Button>
              </section>
              <section class="col results-col">
                <h2>Ditt resultat</h2>
                <!-- <p>Resultatet visar utsläpp av koldioxidekvivalenter för hela drivmedlets livscykel (WTW), energianvändningen för det valda drivmedlet samt den totala kostnaden baserat på mängd och pris.</p> -->
                <div class="results">
                  <div v-if="result">
                    <div>
                      <div v-if="result.co2Impact" class="my-2">
                        <h3 class="font-normal text-base mb-0">WTW Klimatpåverkan</h3>
                        <p class="font-bold text-4xl">
                          {{ formatNumber(result.co2Impact) }} kg CO<sub>2</sub>e
                        </p>
                      </div>
                      <div v-if="result.co2Impact" class="my-2">
                        <h3 class="font-normal text-base mb-0">Energianvändning</h3>
                        <p class="font-bold text-4xl">
                          {{ formatNumber(result.energyContent) }} kWh
                        </p>
                      </div>
                      <div v-if="checked && result.totalCost" class="my-2">
                        <h3 class="font-normal text-base mb-0">Kostnad</h3>
                        <p class="font-bold text-4xl">{{ formatNumber(result.totalCost) }} SEK</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-results">
                    <p>Vänligen fyll i alla fält</p>
                  </div>
                </div>
              </section>
            </div>
            <div class="grid my-2">
              <section v-if="result" class="col">
                <h2>Jämfört med andra drivmedel</h2>
                <p>
                  Här ser du hur ditt val av drivmedel står sig i jämförelse med andra drivmedel när
                  det gäller:
                </p>
                <ul>
                  <li>Klimatpåverkan: Utsläpp av koldioxidekvivalenter.</li>
                  <li>
                    Energimängd: Energiinnehållet för att uppnå samma effekt som din valda mängd.
                  </li>
                </ul>
                <p>Använd knapparna för att växla mellan jämförelser.</p>
                <div class="my-4 flex gap-2">
                  <div>
                    <RadioButton
                      v-model="comparisonDimension"
                      value="energyContent"
                      input-id="co2Impact"
                    />
                    <label class="ml-1" for="co2Impact">Klimatpåverkan</label>
                  </div>
                  <div>
                    <RadioButton
                      v-model="comparisonDimension"
                      value="co2Impact"
                      input-id="energyContent"
                    />
                    <label class="ml-1" for="energyContent">Energiinnehåll</label>
                  </div>
                </div>
                <div class="my-2">
                  <strong v-if="comparisonDimension === 'co2Impact'"
                    >{{ formatNumber(result.co2Impact) }} kg CO₂e
                  </strong>
                  <strong v-if="comparisonDimension === 'energyContent'"
                    >{{ formatNumber(result.energyContent) }} kWh</strong
                  >
                  ({{ selectedAmount }} {{ selectedFuel?.unit }} {{ selectedFuel?.name }}) motsvarar
                </div>
                <DataTable
                  :value="comparison"
                  :sort-field="
                    { co2Impact: 'energyContent', energyContent: 'co2Impact' }[comparisonDimension]
                  "
                  :sort-order="{ co2Impact: -1, energyContent: 1 }[comparisonDimension]"
                  :row-class="(row) => (row.name === selectedFuel?.name ? 'selected' : '')"
                >
                  <Column field="name" header="Drivmedel" sortable>
                    <template #body="{ data }">
                      {{ data?.calculationLabel || data.name }}
                    </template>
                  </Column>
                  <Column field="fuelAmount" header="Bränslemängd" sortable>
                    <template #body="{ data }">
                      {{ formatNumber(data.fuelAmount) }} {{ data.unit }}
                    </template>
                  </Column>
                  <Column
                    :field="
                      { co2Impact: 'energyContent', energyContent: 'co2Impact' }[
                        comparisonDimension
                      ]
                    "
                    :header="
                      comparisonDimension === 'energyContent'
                        ? 'WTW Klimatpåverkan'
                        : 'Energiinnehåll'
                    "
                    sortable
                  >
                    <template #body="{ data }">
                      <span v-if="comparisonDimension === 'energyContent'"
                        >{{ formatNumber(data.co2Impact) }} kg CO₂e</span
                      >
                      <span v-else>{{ formatNumber(data.energyContent) }} kWh</span>
                      <div class="data-bar-wrapper">
                        <div
                          class="data-bar"
                          :style="{
                            width: `${
                              data[
                                {
                                  co2Impact: 'energyContentRelative',
                                  energyContent: 'co2ImpactRelative',
                                }[comparisonDimension]
                              ] * 100
                            }%`,
                          }"
                        ></div>
                      </div>
                    </template>
                  </Column>
                </DataTable>
              </section>
            </div>
            <p>
              Läs mer om beräkningsmodellen
              <NuxtLink href="/kalkylator-info">här</NuxtLink>
            </p>
          </AppCard>
        </div>
      </div>
    </LayoutSection>
    <template v-if="pageInfo?.attributes?.additionalContent">
      <LayoutSection
        v-for="content in pageInfo?.attributes?.additionalContent"
        :key="content.title"
      >
        <div class="additional-content">
          <h2>{{ content?.title }}</h2>
          <p class="ingress">{{ content?.ingress }}</p>
          <MarkdownRenderer :content="content?.richText" />
        </div>
      </LayoutSection>
    </template>
  </main>
</template>
<style scoped lang="scss">
p.ingress {
  font-size: 1.125rem;
  color: var(--gray);
  margin-top: 0.5rem;
}
.additional-content {
  margin-top: 2rem;
  max-width: 60ch;
}
:deep(tr.selected) {
  background-color: color-mix(in srgb, var(--green) 15%, transparent);
}
.data-bar {
  background-color: var(--dark-green);
  height: 0.5rem;
  border-radius: 3px;
}
.data-bar-wrapper {
  max-width: 100%;
  min-width: 100px;
  margin-top: 8px;
  background-color: var(--light-gray);
  border-radius: 3px;
  overflow: hidden;
}
.results-col {
  display: flex;
  flex-direction: column;
}
.results {
  // flex: 1;
  display: flex;
  background-color: var(--green);
  padding: 1rem;
  align-items: center;
  border-radius: 8px;
}
.no-results {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 100%;
}
.help-block {
  font-size: 0.75rem;
  color: var(--gray);
  margin-top: 0.5rem;
  font-weight: 500;
}
</style>
